import { computed, getCurrentScope, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useSettingsStore } from '@/stores/settings';
import { useFavoritesStore } from '@/stores/favorites';
import { useEpgStore } from '@/stores/epg';
import { log } from '@/lib/log';
import {
    EPG_NOTIFICATION_HORIZON_MS,
    EPG_NOTIFICATION_LEAD_MS,
    EPG_NOTIFICATION_REPLAN_INTERVAL_MS,
    leadPhrase,
    liveDeepLink,
    liveKey,
    notificationClickTarget,
    planEpgNotifications,
    startsInPhrase,
    type EpgNotificationPlanEntry,
    type PlannableProgramme,
} from '@/lib/epgNotificationPlan';

// EPG start notifications (Spec 10) — the IMPURE half.
//
// Port of `legacy-public/js/components/EpgNotifier.js`. The decision — which
// programmes, at what instant — is pure and lives in
// `lib/epgNotificationPlan.ts`. Everything a test cannot express without a
// browser lives here: timers, `Notification.permission`, the Notification
// itself, and the fetch that feeds the planner.
//
// Two consumers, one scheduler:
//   • `App.vue` calls `useEpgNotifications({ schedule: true, navigate })` —
//     the app shell is the only place a scheduler can live and still fire
//     while the user is on /movies. It never unmounts in practice, but the
//     teardown path is wired anyway.
//   • `views/settings/PlayerTab.vue` calls `useEpgNotifications()` (no
//     scheduling) purely for the permission state + the gesture-bound
//     request. Permission state is module-level so both instances agree.
//
// PERMISSION IS PER-BROWSER; THE SETTING IS SERVER-WIDE. `epgNotificationsEnabled`
// lives in `/api/settings` (admin-write, shared by every account), while
// `Notification.permission` is granted per browser profile per origin. Those
// are two independent facts and the UI must never collapse them into one
// "on" — see `describeEpgNotificationState` at the bottom, which is the one
// place the copy for all six combinations is decided.

export type EpgNotificationPermission = NotificationPermission | 'unsupported';

const SETTING_KEY = 'epgNotificationsEnabled';
/** Manifest icons (`web/public/manifest.webmanifest`). The legacy `/favicon.svg` no longer exists. */
const NOTIFICATION_ICON = '/icons/pwa-192x192.png';
const NOTIFICATION_BADGE = '/icons/pwa-64x64.png';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------
//
// Shared deliberately: two composable instances must not run two schedulers
// (duplicate notifications) and must not disagree about permission. Drained
// by `_resetEpgNotificationsForTests` — every module-level singleton in this
// codebase needs an afterEach drain hook (Phase 4 retro #5).

/** Last-read `Notification.permission`. Re-read on every replan and at fire time. */
const permission = ref<EpgNotificationPermission>('unsupported');
/** key → timeout handle for every ARMED, not-yet-fired notification. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** key → programme `startMs` for every notification already SHOWN. Pruned on each replan. */
const notified = new Map<string, number>();
let replanHandle: ReturnType<typeof setInterval> | null = null;
/** Guards against a second scheduling instance arming a parallel set of timers. */
let schedulerClaimed = false;
/** Bumped on every replan; an in-flight replan whose token is stale drops its result. */
let replanSeq = 0;

function readPermission(): EpgNotificationPermission {
    if (typeof Notification === 'undefined') return 'unsupported';
    const raw = Notification.permission;
    return raw === 'granted' || raw === 'denied' ? raw : 'default';
}

function cancelPending(): void {
    for (const handle of timers.values()) clearTimeout(handle);
    timers.clear();
}

/**
 * Drop `notified` entries whose programme started more than one horizon ago.
 * By then the planner's own `fireAt <= now` filter already excludes them, so
 * the bookkeeping is dead weight — and without pruning, a long-lived PWA
 * session grows the map by every favourite's every programme.
 */
function pruneNotified(now: number): void {
    const cutoff = now - EPG_NOTIFICATION_HORIZON_MS;
    for (const [key, startMs] of notified) {
        if (startMs < cutoff) notified.delete(key);
    }
}

/**
 * The active service-worker registration, or null.
 *
 * WHY THE SW MATTERS FOR CLICKS: a notification created with
 * `new Notification()` from a document dispatches its click to that
 * document's own `Notification.onclick` and NEVER to the service worker's
 * `notificationclick` event — so `web/src/sw.ts`'s handler (focus an
 * existing tab, postMessage the target) only ever runs for notifications
 * created via `registration.showNotification()`. That is the path we prefer,
 * because it is also the ONLY path that works at all on Android Chrome
 * (`new Notification()` throws "Illegal constructor" there) and the only one
 * whose click still works after the originating tab is closed.
 *
 * `reg.active` is required, not just `reg`: an installing/waiting worker has
 * no activated scope to deliver `notificationclick` to.
 */
async function activeServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        return reg?.active ? reg : null;
    } catch (err) {
        log.warn('epg_notifications.sw_lookup_failed', { error: (err as Error).message });
        return null;
    }
}

export interface UseEpgNotificationsOptions {
    /**
     * Run the scheduler (timers + replan interval). Exactly ONE call site
     * should pass `true` — the app shell. Defaults to false so a Settings
     * mount cannot arm a second set of timers.
     */
    schedule?: boolean;
    /**
     * In-page click navigation for the `new Notification()` FALLBACK path
     * only (no active service worker — Vite dev has Serwist disabled, and a
     * registration can fail). The service-worker path routes through
     * `main.ts`'s `epg-notification-click` message listener instead; both
     * resolve the destination with the same `notificationClickTarget()`
     * helper so there is one resolver, not two.
     */
    navigate?: (path: string) => void;
}

export interface UseEpgNotificationsResult {
    /** Last-read browser permission, or `'unsupported'` when there is no Notification API. */
    permission: Ref<EpgNotificationPermission>;
    /** `epgNotificationsEnabled` from `/api/settings` — server-wide, says nothing about this browser. */
    settingEnabled: ComputedRef<boolean>;
    /** True only when the server setting is on AND this browser granted permission. */
    active: ComputedRef<boolean>;
    /** Count of armed, not-yet-fired notifications. Diagnostics + tests. */
    scheduledCount: Ref<number>;
    /** Re-read `Notification.permission`. Cheap; safe to call from a template event. */
    syncPermission: () => EpgNotificationPermission;
    /**
     * Ask the browser for permission. **MUST be called synchronously from a
     * real user gesture** — every current browser ignores (Chrome) or
     * auto-rejects (Safari) a request that is not user-activated, and Chrome
     * additionally never re-prompts once the state is `'denied'`. Returns the
     * resulting state; a `'denied'` result is final until the user changes it
     * in the browser's own site settings.
     */
    requestPermission: () => Promise<EpgNotificationPermission>;
    /** Force a replan now. Exposed for tests and for a future manual "check again". */
    replan: () => Promise<void>;
}

export function useEpgNotifications(opts: UseEpgNotificationsOptions = {}): UseEpgNotificationsResult {
    const settings = useSettingsStore();
    const favorites = useFavoritesStore();
    const epg = useEpgStore();

    const settingEnabled = computed<boolean>(() => settings.get<boolean>(SETTING_KEY, false));
    const active = computed<boolean>(() => settingEnabled.value && permission.value === 'granted');
    const scheduledCount = ref<number>(timers.size);

    /**
     * Stable signature of the favourite CHANNEL set. A replan is triggered by
     * this string changing, not by the favourites array identity: the store
     * replaces the array on every add/remove AND on every `refresh()`, and
     * refetching the EPG window for an unchanged set on each of those would
     * be pure waste.
     */
    const favouriteSignature = computed<string>(() => {
        const keys: string[] = [];
        for (const f of favorites.favorites) {
            if (f.itemType !== 'channel') continue;
            keys.push(liveKey(f.sourceId, f.itemId));
        }
        keys.sort();
        return keys.join(',');
    });

    function syncPermission(): EpgNotificationPermission {
        permission.value = readPermission();
        return permission.value;
    }

    async function requestPermission(): Promise<EpgNotificationPermission> {
        if (typeof Notification === 'undefined') {
            permission.value = 'unsupported';
            return 'unsupported';
        }
        // Only 'default' can produce a prompt. Calling requestPermission on a
        // 'denied' origin resolves 'denied' immediately without any UI — the
        // browser will not ask again, and the user has to change it in site
        // settings. Short-circuiting keeps that honest rather than looking
        // like a request that "failed".
        if (Notification.permission !== 'default') return syncPermission();
        try {
            const result = await Notification.requestPermission();
            permission.value = result === 'granted' || result === 'denied' ? result : 'default';
        } catch (err) {
            log.warn('epg_notifications.permission_request_failed', { error: (err as Error).message });
            syncPermission();
        }
        log.info('epg_notifications.permission', { state: permission.value });
        return permission.value;
    }

    async function showNotification(entry: EpgNotificationPlanEntry): Promise<void> {
        const url = liveDeepLink(entry.sourceId, entry.channelId);
        const wait = startsInPhrase(entry.startMs, Date.now());
        const body = entry.channelName
            ? `${entry.channelName} — ${wait}`
            : `${wait.charAt(0).toUpperCase()}${wait.slice(1)}`;
        const options: NotificationOptions = {
            body,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_BADGE,
            // Tag on the full identity so a stale duplicate (a second tab,
            // a re-armed timer we failed to cancel) REPLACES rather than
            // stacks. Legacy tagged per channel, which collapsed two
            // genuinely different programmes on the same channel.
            tag: entry.key,
            data: { url, sourceId: entry.sourceId, channelId: entry.channelId, startMs: entry.startMs },
        };

        const reg = await activeServiceWorker();
        if (reg) {
            await reg.showNotification(entry.title, options);
            return;
        }
        const notification = new Notification(entry.title, options);
        notification.onclick = () => {
            try {
                window.focus();
            } catch {
                // Some browsers refuse window.focus() outside a gesture — the
                // navigation below is the part that matters.
            }
            notification.close();
            opts.navigate?.(notificationClickTarget(options.data));
        };
    }

    function fire(entry: EpgNotificationPlanEntry): void {
        timers.delete(entry.key);
        scheduledCount.value = timers.size;
        // Re-read permission at FIRE time. The timer has been armed for up to
        // `HORIZON - LEAD` (9 minutes) and a revocation in the browser's own
        // site settings raises no event we can watch, so this is the ONLY
        // guard that sees it.
        //
        // There is deliberately NO matching re-check of the setting here. The
        // `active` watcher below already cancels every armed timer when the
        // setting flips off, and it cannot lose that race: a reactive change
        // flushes Vue's pre-queue in a microtask, and microtasks always drain
        // before the next macrotask (which is where a due `setTimeout`
        // callback runs). A second check would be unreachable code, i.e. a
        // guard no test could ever kill — the cancellation is pinned by
        // "cancels pending timers when the setting flips off" instead.
        if (syncPermission() !== 'granted') return;
        // Recorded only once we are actually going to show it — a key parked
        // in `notified` for a notification nobody saw would both suppress a
        // legitimate retry and make the `suppressed:` count below a lie.
        notified.set(entry.key, entry.startMs);
        void showNotification(entry)
            .then(() => {
                log.info('epg_notifications.fired', {
                    sourceId: entry.sourceId,
                    channelId: entry.channelId,
                    startMs: entry.startMs,
                });
            })
            .catch((err: unknown) => {
                log.warn('epg_notifications.fire_failed', { error: (err as Error).message });
            });
    }

    function arm(entry: EpgNotificationPlanEntry): void {
        if (timers.has(entry.key)) return;
        const delay = Math.max(0, entry.fireAt - Date.now());
        timers.set(entry.key, setTimeout(() => fire(entry), delay));
    }

    async function replan(): Promise<void> {
        const seq = ++replanSeq;
        // Permission can have been revoked since the last pass; re-reading
        // here is what makes `active` (and therefore the whole scheduler)
        // notice without polling.
        syncPermission();
        if (!active.value) {
            cancelPending();
            scheduledCount.value = 0;
            return;
        }
        if (!favorites.loaded) {
            try {
                await favorites.refresh();
            } catch (err) {
                log.warn('epg_notifications.favorites_refresh_failed', { error: (err as Error).message });
                return;
            }
            if (seq !== replanSeq) return;
        }

        const favouriteLiveKeys = new Set<string>();
        for (const f of favorites.favorites) {
            if (f.itemType !== 'channel') continue;
            favouriteLiveKeys.add(liveKey(f.sourceId, f.itemId));
        }
        if (favouriteLiveKeys.size === 0) {
            cancelPending();
            scheduledCount.value = 0;
            log.debug('epg_notifications.no_favourites');
            return;
        }

        const fetchFrom = Date.now();
        await epg.loadNotifierWindow(
            [...favouriteLiveKeys],
            fetchFrom,
            fetchFrom + EPG_NOTIFICATION_HORIZON_MS,
        );
        if (seq !== replanSeq) return;
        if (!active.value) {
            cancelPending();
            scheduledCount.value = 0;
            return;
        }

        // SYNCHRONOUS from here to the last `arm()`. Cancelling before the
        // fetch (which is what the legacy notifier did — EpgNotifier.js:95)
        // opens a hole the width of the request: a notification due to fire
        // 200ms from now loses its timer, and by the time the response lands
        // its `fireAt` is in the past, so the replan drops it too and it
        // never fires at all.
        cancelPending();
        const now = Date.now();
        pruneNotified(now);
        // The channel NAME is what makes the notification identifiable across
        // several favourites, and it is not on a programme row — the server's
        // `formatProgramme` never emits one. `loadNotifierWindow` captures it
        // from the same payload's `channels[]`, and this is where the two are
        // joined.
        const names = epg.notifierChannelNames;
        const plannable: PlannableProgramme[] = epg.notifierProgrammes.map((p) => ({
            sourceId: p.sourceId,
            channelId: p.channelId,
            startMs: p.startMs,
            title: p.title,
            channelName: p.liveId ? names.get(p.liveId) ?? null : null,
        }));
        const plan = planEpgNotifications({
            programmes: plannable,
            favouriteLiveKeys,
            now,
            alreadyNotified: new Set(notified.keys()),
        });
        for (const entry of plan) arm(entry);
        scheduledCount.value = timers.size;
        log.info('epg_notifications.replanned', {
            favourites: favouriteLiveKeys.size,
            programmes: epg.notifierProgrammes.length,
            named: plan.filter((e) => e.channelName !== null).length,
            scheduled: plan.length,
            suppressed: notified.size,
        });
    }

    function start(): void {
        if (replanHandle !== null) return;
        replanHandle = setInterval(() => void replan(), EPG_NOTIFICATION_REPLAN_INTERVAL_MS);
        log.info('epg_notifications.started', {
            horizonMs: EPG_NOTIFICATION_HORIZON_MS,
            leadMs: EPG_NOTIFICATION_LEAD_MS,
            replanMs: EPG_NOTIFICATION_REPLAN_INTERVAL_MS,
        });
        void replan();
    }

    function stop(): void {
        // `watch(active, …, {immediate: true})` runs this at App setup, before
        // settings have loaded, for the ~every session where the feature is
        // off. Logging unconditionally put one meaningless line per session on
        // the batched /api/clientlog stream.
        const wasRunning = replanHandle !== null || timers.size > 0;
        if (replanHandle !== null) {
            clearInterval(replanHandle);
            replanHandle = null;
        }
        cancelPending();
        scheduledCount.value = 0;
        if (wasRunning) log.info('epg_notifications.stopped');
    }

    syncPermission();

    let scheduling = opts.schedule === true;
    if (scheduling && schedulerClaimed) {
        // Not fatal, but it means two call sites both think they own the
        // scheduler — the second would double every notification. Refuse and
        // say so loudly rather than silently duplicating.
        log.warn('epg_notifications.scheduler_already_claimed');
        scheduling = false;
    }

    if (scheduling) {
        schedulerClaimed = true;
        watch(active, (on) => (on ? start() : stop()), { immediate: true });
        // Favourites changing is a genuine replan trigger: a channel
        // favourited a minute ago should be able to notify for a programme
        // starting in five, without waiting out the replan interval.
        watch(favouriteSignature, () => {
            if (active.value) void replan();
        });
        if (getCurrentScope()) {
            onScopeDispose(() => {
                stop();
                schedulerClaimed = false;
            });
        }
    }

    // Permission can change in the browser's own site settings while the tab
    // sits open — there is no event for it, but coming back to the tab is the
    // moment the user would expect the UI to be truthful again. Registered
    // per instance and torn down with the instance; both write the same
    // module-level ref, so overlap is idempotent.
    if (getCurrentScope() && typeof document !== 'undefined') {
        const onVisible = () => {
            if (document.visibilityState === 'visible') syncPermission();
        };
        document.addEventListener('visibilitychange', onVisible);
        onScopeDispose(() => document.removeEventListener('visibilitychange', onVisible));
    }

    return {
        permission,
        settingEnabled,
        active,
        scheduledCount,
        syncPermission,
        requestPermission,
        replan,
    };
}

export type EpgNotificationTone = 'ok' | 'info' | 'warn';

export interface EpgNotificationStateDescription {
    tone: EpgNotificationTone;
    /** Always-visible status line. */
    text: string;
    /** True when the state deserves `role="alert"` — the setting is on but this browser will show nothing. */
    alert: boolean;
    /** True when a "Allow notifications" button can still produce a prompt (permission is `'default'`). */
    canRequest: boolean;
}

/**
 * The one place all eight (setting × permission) combinations get their copy —
 * two setting states by four permission states, `'unsupported'` included.
 *
 * Pure so the honesty of the control is a unit test rather than a screenshot
 * judgement. The rule it encodes: never let the toggle's position be the only
 * thing the user reads. The toggle reflects the SERVER setting; this line
 * says what THIS BROWSER will actually do — and when those two disagree, it
 * says so as an alert, because "on" with no notifications arriving is
 * precisely the dishonest control this codebase is purging.
 */
export function describeEpgNotificationState(input: {
    settingEnabled: boolean;
    permission: EpgNotificationPermission;
    /**
     * True for a viewer: `epgNotificationsEnabled` is admin-write, so the
     * off-state copy must not tell them to "switch this on" — the control is
     * disabled for them. Permission is still theirs to grant, so the Allow
     * button stays reachable whenever the SETTING is on.
     */
    readonly?: boolean;
}): EpgNotificationStateDescription {
    const { settingEnabled, permission: perm } = input;
    const readonly = input.readonly === true;
    if (perm === 'unsupported') {
        return {
            tone: 'warn',
            text: settingEnabled
                ? 'Enabled on the server, but this browser has no notification support — nothing will be shown here.'
                : 'Off. This browser has no notification support, so it could not show these anyway.',
            alert: settingEnabled,
            canRequest: false,
        };
    }
    if (perm === 'denied') {
        return {
            tone: 'warn',
            text: settingEnabled
                ? 'Enabled on the server, but this browser is blocking notifications for this site — you will not get any here. Browsers do not ask twice: allow them in this site’s browser settings, then reload.'
                : 'This browser is blocking notifications for this site. Browsers do not ask twice — allow them in this site’s browser settings, then reload.',
            alert: settingEnabled,
            canRequest: false,
        };
    }
    if (perm === 'default') {
        return {
            tone: settingEnabled ? 'warn' : 'info',
            text: settingEnabled
                ? 'Enabled on the server, but this browser has not been asked yet — nothing will be shown here until you allow notifications.'
                : readonly
                    ? 'Off. Only an administrator can turn this on.'
                    : 'Off. Switching this on also asks this browser for permission to show notifications.',
            alert: settingEnabled,
            // Granting permission for a feature that is off AND that this user
            // cannot turn on buys them nothing — offering the button would be
            // the same kind of empty affordance as the toggle itself.
            canRequest: settingEnabled || !readonly,
        };
    }
    return {
        tone: settingEnabled ? 'ok' : 'info',
        text: settingEnabled
            ? `On. This browser will show a reminder ${leadPhrase()} before a favourite channel’s next programme starts.`
            : readonly
                ? 'Off. Only an administrator can turn this on.'
                : 'Off. This browser has already allowed notifications, so switching this on takes effect straight away.',
        alert: false,
        canRequest: false,
    };
}

/**
 * Tear the scheduler down from outside a component scope.
 *
 * Exists for the auth lifecycle: `App.vue` calls it when `auth.isAuthed` goes
 * false. Today both logout buttons and the API client's 401 path hard-navigate
 * (`location.href = '/login'`), which kills the document and every timer with
 * it — so this is insurance, not a live fix. It stops being insurance the
 * moment logout becomes a client-side `router.replace`, at which point user
 * A's armed timers would fire for user B and the replan loop would keep
 * polling with A's favourites. This is the one per-user module-level singleton
 * that App.vue's logout branch did not drain.
 */
export function stopEpgNotificationScheduler(): void {
    for (const handle of timers.values()) clearTimeout(handle);
    timers.clear();
    notified.clear();
    if (replanHandle !== null) {
        clearInterval(replanHandle);
        replanHandle = null;
    }
    replanSeq++;
}

/**
 * Test hook — drain every module-level singleton so the next
 * `useEpgNotifications()` starts clean (Phase 4 retro #5).
 */
export function _resetEpgNotificationsForTests(): void {
    cancelPending();
    notified.clear();
    if (replanHandle !== null) {
        clearInterval(replanHandle);
        replanHandle = null;
    }
    schedulerClaimed = false;
    replanSeq = 0;
    permission.value = 'unsupported';
}
